
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';

// Common English stopwords plus a few generic job-posting filler words that would
// otherwise pollute the keyword list ("responsibilities", "requirements", etc.).
const STOPWORDS = new Set([
  'a','an','the','and','or','but','if','then','else','for','to','of','in','on','at','by','with','from',
  'as','is','are','was','were','be','been','being','it','this','that','these','those','you','your','we',
  'our','they','their','he','she','his','her','its','i','me','my','who','whom','which','what','will',
  'would','can','could','should','shall','may','might','must','not','no','yes','do','does','did','have',
  'has','had','having','into','about','than','so','such','too','very','also','more','most','some','any',
  'all','each','other','out','up','down','over','under','again','further','once','here','there','when',
  'where','why','how','job','role','work','working','team','company','years','year','experience','strong',
  'ability','skills','including','required','requirements','responsibilities','responsible','preferred',
  'plus','etc','using','use','used','across','within','join','looking','opportunity','candidate','candidates',
  'apply','ideal','excellent','good','strong','knowledge','understanding','proficient','proficiency',
  'demonstrated','ensure','ensuring','help','helping','support','supporting','new','various','related',
  'per','week','month','day','time','full','part','remote','onsite','hybrid',
]);

// Pulls unigrams and bigrams out of free text, ranked by frequency. Bigrams catch
// multi-word skills ("machine learning", "project management") that single-word
// matching would miss.
function extractKeywords(text, limit = 22) {
  const tokens = (text.toLowerCase().match(/[a-z0-9+#]+(?:\.[a-z0-9]+)*/g) || []).filter(
    (t) => t.length >= 2 && !STOPWORDS.has(t)
  );

  const unigramFreq = new Map();
  tokens.forEach((t) => unigramFreq.set(t, (unigramFreq.get(t) || 0) + 1));

  const bigramFreq = new Map();
  const rawTokens = text.toLowerCase().match(/[a-z0-9+#]+(?:\.[a-z0-9]+)*/g) || [];
  for (let i = 0; i < rawTokens.length - 1; i++) {
    const a = rawTokens[i];
    const b = rawTokens[i + 1];
    if (a.length >= 2 && b.length >= 2 && !STOPWORDS.has(a) && !STOPWORDS.has(b)) {
      const phrase = `${a} ${b}`;
      bigramFreq.set(phrase, (bigramFreq.get(phrase) || 0) + 1);
    }
  }

  const bigrams = [...bigramFreq.entries()]
    .filter(([, count]) => count >= 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, Math.ceil(limit * 0.4))
    .map(([phrase]) => phrase);

  const bigramWords = new Set(bigrams.flatMap((p) => p.split(' ')));

  const unigrams = [...unigramFreq.entries()]
    .filter(([word]) => !bigramWords.has(word))
    .sort((a, b) => b[1] - a[1])
    .map(([word]) => word);

  const combined = [...bigrams, ...unigrams].slice(0, limit);
  return combined;
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function keywordInCorpus(keyword, corpus) {
  const pattern = new RegExp(`\\b${escapeRegExp(keyword)}\\b`, 'i');
  return pattern.test(corpus);
}

// Accepts a `size` prop so the caller can shrink the gauge on narrow viewports.
function ScoreGauge({ score, size = 116, stroke = 11 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, score)) / 100) * circumference;
  const color = score >= 75 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
  const label = score >= 75 ? 'Strong Match' : score >= 50 ? 'Fair Match' : 'Needs Work';

  return (
    <div className="relative shrink-0 mx-auto sm:mx-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F3E8FF" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.4s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl sm:text-2xl font-extrabold text-[#0D1B3D] tabular-nums">{score}%</span>
        <span className="text-[9px] font-bold tracking-wide uppercase mt-0.5" style={{ color }}>{label}</span>
      </div>
    </div>
  );
}

// Some list endpoints may wrap results in { data: [...] } (e.g. Laravel API
// Resources / pagination) instead of returning a bare array. This normalizes
// either shape to a plain array so .map()/.length never blow up downstream.
function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

function getPhotoUrl(profile) {
  if (!profile) return null;
  if (profile.profile_photo_url) return profile.profile_photo_url;
  if (!profile.profile_photo_path) return null;
  const apiOrigin = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${apiOrigin}/storage/${profile.profile_photo_path}`;
}

function getInitials(name) {
  if (!name) return '';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
}

function ResumeBuilder({ view = 'resume' }) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [educations, setEducations] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // ATS Score Checker (Phase 8 - Premium Features)
  const [jobDescription, setJobDescription] = useState('');
  const [atsResult, setAtsResult] = useState(null);
  const [checkingAts, setCheckingAts] = useState(false);
  const [atsError, setAtsError] = useState('');

  // Interview Questions (resume based)
const [targetRole, setTargetRole] = useState('');
const [questions, setQuestions] = useState([]);
const [loadingQuestions, setLoadingQuestions] = useState(false);
const [questionsError, setQuestionsError] = useState('');
const [difficulty, setDifficulty] = useState('normal');
const [interviewJd, setInterviewJd] = useState('');

// Mock Interview
const [mockActive, setMockActive] = useState(false);
const [mockIndex, setMockIndex] = useState(0);
const [mockAnswer, setMockAnswer] = useState('');
const [mockFeedback, setMockFeedback] = useState(null);
const [mockLoading, setMockLoading] = useState(false);
const [mockError, setMockError] = useState('');
const [mockResults, setMockResults] = useState([]); // one entry per question
const [mockDone, setMockDone] = useState(false);


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [profileRes, skillsRes, eduRes, expRes, certRes, langRes] = await Promise.all([
          api.get('/profile'),
          api.get('/skills'),
          api.get('/educations'),
          api.get('/experiences'),
          api.get('/certifications'),
          api.get('/languages'),
        ]);
        setProfile(profileRes.data);
        setSkills(toArray(skillsRes.data));
        setEducations(toArray(eduRes.data));
        setExperiences(toArray(expRes.data));
        setCertifications(toArray(certRes.data));
        setLanguages(toArray(langRes.data));
      } catch (err) {
        console.error('Failed to load resume data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [navigate, refreshKey]);

  useEffect(() => {
    if (loading || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [loading, hash]);

  // Flattens every resume field into one lowercase string used as the match corpus.
  const resumeCorpus = useMemo(() => {
    const parts = [
      profile?.career_objective,
      ...skills.map((s) => s.skill_name),
      ...experiences.flatMap((e) => [e.job_title, e.company_name, e.description]),
      ...educations.flatMap((e) => [e.degree, e.institution, e.board_university]),
      ...certifications.flatMap((c) => [c.certificate_name, c.issued_by]),
      ...languages.map((l) => l.language_name),
    ];
    return parts.filter(Boolean).join(' ').toLowerCase();
  }, [profile, skills, experiences, educations, certifications, languages]);

     const handleCheckAts = async () => {
    if (!jobDescription.trim()) return;
    setCheckingAts(true);
    setAtsError('');
    setAtsResult(null);
    try {
      const res = await api.post('/profile/ats-score', { job_description: jobDescription });
      const matched = res.data.matched_keywords || [];
      const missing = res.data.missing_keywords || [];
      setAtsResult({
        score: Math.round(res.data.score || 0),
        matched,
        missing,
        total: matched.length + missing.length,
        suggestions: res.data.suggestions || [],
      });
    } catch (err) {
      console.error('ATS API failed:', err.response?.status, err.response?.data || err.message);
      setAtsError(
        err.response?.data?.message || 'Could not check your resume right now. Please try again.'
      );
    } finally {
      setCheckingAts(false);
    }
  };

  const handleGenerateQuestions = async () => {
  setLoadingQuestions(true);
  setQuestionsError('');
  try {
    const res = await api.post('/ai/interview-questions', {
      difficulty,
      job_description: interviewJd.trim(),
      target_role: targetRole.trim() || experiences[0]?.job_title || '',
      career_objective: profile?.career_objective || '',
      skills: skills.map((s) => s.skill_name).filter(Boolean),
      experiences: experiences.map((e) => ({
        job_title: e.job_title,
        company_name: e.company_name,
        description: e.description,
      })),
      educations: educations.map((e) => ({
        degree: e.degree,
        institution: e.institution,
      })),
      certifications: certifications.map((c) => c.certificate_name).filter(Boolean),
    });
    const list = res.data.questions || [];
    setQuestions(list);
    setMockActive(false);
    return list;
  } catch (err) {
    setQuestions([]);
    setQuestionsError(
      err.response?.data?.message || 'Could not generate questions right now. Please try again.'
    );
    return [];
  } finally {
    setLoadingQuestions(false);
  }
};



// Group questions by category for display
const groupedQuestions = questions.reduce((acc, q) => {
  (acc[q.category] = acc[q.category] || []).push(q);
  return acc;
}, {});

const handleStartMock = async () => {
  let list = questions;
  if (list.length === 0) {
    list = await handleGenerateQuestions();
    if (list.length === 0) return;
  }
  setMockActive(true);
  setMockIndex(0);
  setMockAnswer('');
  setMockFeedback(null);
  setMockError('');
  setMockResults([]);
  setMockDone(false);
};

const handleExitMock = () => {
  setMockActive(false);
  setMockDone(false);
    setQuestions([]);
};

const handleSubmitAnswer = async () => {
  const current = questions[mockIndex];
  if (!current || mockAnswer.trim().length < 5) return;

  setMockLoading(true);
  setMockError('');
  try {
    const res = await api.post('/ai/mock-interview-feedback', {
      question: current.question,
      category: current.category,
      answer: mockAnswer.trim(),
      target_role: targetRole.trim() || experiences[0]?.job_title || '',
    });
    setMockFeedback(res.data);
    setMockResults((prev) => {
      const copy = [...prev];
      copy[mockIndex] = {
        question: current.question,
        category: current.category,
        score: res.data.score,
      };
      return copy;
    });
  } catch (err) {
    setMockError(
      err.response?.data?.message || 'Could not evaluate your answer right now. Please try again.'
    );
  } finally {
    setMockLoading(false);
  }
};

const handleSkipQuestion = () => {
  const current = questions[mockIndex];
  setMockResults((prev) => {
    const copy = [...prev];
    copy[mockIndex] = { question: current.question, category: current.category, score: null };
    return copy;
  });
  handleNextQuestion();
};

const handleNextQuestion = () => {
  if (mockIndex + 1 < questions.length) {
    setMockIndex((i) => i + 1);
    setMockAnswer('');
    setMockFeedback(null);
    setMockError('');
  } else {
    setMockDone(true);
  }
};

// Average only over questions that were actually answered
const mockScored = mockResults.filter((r) => r && r.score != null);
const mockAverage = mockScored.length
  ? (mockScored.reduce((sum, r) => sum + r.score, 0) / mockScored.length).toFixed(1)
  : 0;

  const handleDownload = () => {
    window.print();
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;
  if (!profile) return null;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <Navbar user={user} />

      <div className="flex-1 w-full max-w-3xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 print:hidden">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
  {view === 'ats' ? 'Resume Score Checker' : view === 'questions' ? 'Interview Q&A' : view === 'mock' ? 'Mock Interview' : 'Resume Preview'}
</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/profile" className="text-purple-600 font-semibold hover:underline sm:self-center text-center sm:text-left">
              Edit Profile
            </Link>
            {view === 'resume' && (
              <button
                onClick={handleDownload}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold shadow-sm hover:opacity-90 transition w-full sm:w-auto"
              >
                Download PDF
              </button>
            )}
          </div>
        </div>

        {/* ATS Score Checker (Phase 8 - Premium Features) */}
        {view === 'ats' && (
        <div id="ats-score" className="scroll-mt-24 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 print:hidden">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                <path d="M9 11l3 3 8-8" />
                <path d="M21 12v6a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-base bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">ATS Score</h3>
              <p className="text-xs text-gray-500">See how well your resume matches a job description.</p>
            </div>
          </div>

                   {!profile.resume_path ? (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-amber-800">Please add your resume first</p>
              <p className="text-xs text-amber-700 mt-1">
                The score is checked against the resume you upload in your Profile (PDF or DOCX).
              </p>
              <Link
                to="/profile"
                className="inline-block mt-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-full font-semibold text-sm hover:opacity-90 transition"
              >
                Add resume in Profile
              </Link>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-3">
                Checking against your uploaded resume:{' '}
                <button
                  type="button"
                  onClick={() =>
                    window.open(
                      profile.resume_path.startsWith('http')
                        ? profile.resume_path
                        : `${BACKEND_URL}/storage/${profile.resume_path}`,
                      '_blank'
                    )
                  }
                  className="text-purple-600 font-semibold hover:underline"
                >
                  View
                </button>
              </p>
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={5}
                placeholder="Paste a job description here to check your resume's match..."
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
              <button
                onClick={handleCheckAts}
                disabled={checkingAts || jobDescription.trim().length < 20}
                className="mt-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold text-sm shadow-sm hover:opacity-90 transition disabled:opacity-50 w-full sm:w-auto"
              >
                {checkingAts ? 'Checking...' : 'Check ATS Score'}
              </button>
              {atsError && <p className="mt-3 text-sm text-red-500">{atsError}</p>}
            </div>
          )}

          {atsResult && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row gap-6 sm:items-start">
                <ScoreGauge score={atsResult.score} size={96} />

                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 mb-4">
                    Matched <strong className="text-[#0D1B3D]">{atsResult.matched.length}</strong> of{' '}
                    <strong className="text-[#0D1B3D]">{atsResult.total}</strong> key terms from this job description.
                  </p>

                  {atsResult.matched.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Matched Keywords
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {atsResult.matched.map((kw) => (
                          <span
                            key={kw}
                            className="bg-[#ECFDF5] text-[#10B981] text-xs font-medium px-3 py-1 rounded-full"
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {atsResult.missing.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Missing Keywords
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {atsResult.missing.map((kw) => (
                          <span
                            key={kw}
                            className="bg-red-50 text-red-500 text-xs font-medium px-3 py-1 rounded-full"
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-3">
                        Consider weaving relevant terms above into your skills, experience, or career objective —
                        only add ones that genuinely apply to you.
                      </p>
                    </div>
                  )}

                  {atsResult.suggestions?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Suggestions</p>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                        {atsResult.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        )}

        {(view === 'questions' || view === 'mock') && (
        <>
        {/* Interview Questions (resume based) */}
{view === 'questions' && (
<div id="interview-questions" className="scroll-mt-24 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 print:hidden">
  <div className="flex items-center gap-3 mb-1">
    <div className="w-11 h-11 rounded-xl bg-[#FDF2F8] text-[#DB2777] flex items-center justify-center shrink-0">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    </div>
    <div>
      <h3 className="font-bold text-base bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Interview Questions</h3>
      <p className="text-xs text-gray-500">Get likely interview questions based on your resume.</p>
    </div>
  </div>

  <div className="mt-4">
    <input
      type="text"
      value={targetRole}
      onChange={(e) => setTargetRole(e.target.value)}
      placeholder="Target role (optional), e.g. Laravel Developer"
      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
    />
        <div className="mt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Difficulty</p>
      <div className="inline-flex bg-gray-100 rounded-full p-1">
        {[
          { key: 'easy', label: 'Easy' },
          { key: 'normal', label: 'Normal' },
          { key: 'hard', label: 'Hard' },
        ].map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => setDifficulty(d.key)}
            disabled={loadingQuestions}
            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition disabled:opacity-60 ${
              difficulty === d.key
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm'
                : 'text-gray-600 hover:text-purple-700'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>
    </div>

       <div className="mt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Job description (optional)</p>
      <textarea
        value={interviewJd}
        onChange={(e) => setInterviewJd(e.target.value)}
        rows={4}
        maxLength={4000}
        disabled={loadingQuestions}
        placeholder="Paste a job description to get questions tailored to that job..."
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:bg-gray-50"
      />
    </div>

    <button
      onClick={handleGenerateQuestions}
      disabled={loadingQuestions}
      className="mt-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold text-sm shadow-sm hover:opacity-90 transition disabled:opacity-50 w-full sm:w-auto"
    >
      {loadingQuestions ? 'Generating...' : questions.length ? 'Regenerate Questions' : 'Generate Interview Questions'}
    </button>
  </div>

  {questionsError && (
    <p className="mt-4 text-sm text-red-500">{questionsError}</p>
  )}

  {questions.length > 0 && (
    <div className="mt-6 pt-6 border-t border-gray-100 space-y-5">
      {Object.entries(groupedQuestions).map(([category, items]) => (
        <div key={category}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{category}</p>
          <div className="space-y-3">
            {items.map((q, i) => (
              <div key={i} className="bg-purple-50/50 border border-purple-100 rounded-xl p-3">
                <p className="text-sm font-semibold text-[#0D1B3D]">{q.question}</p>
                {q.tip && <p className="text-xs text-gray-500 mt-1">Tip: {q.tip}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

  <div className="pt-2">
      <Link
        to="/mock-interview"
        className="inline-block w-full sm:w-auto text-center border-2 border-purple-500 text-purple-700 px-6 py-2 rounded-full font-semibold text-sm hover:bg-purple-50 transition"
      >
        Go to Mock Interview
      </Link>
    </div>

    </div>
  )}
</div>

)}

{/* Mock Interview start screen */}
{view === 'mock' && !mockActive && (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 print:hidden">
    <h3 className="font-bold text-base bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
      Practice a mock interview
    </h3>
    <p className="text-xs text-gray-500 mt-1">Answer questions based on your resume and get a score on each answer.</p>

    <input
      type="text"
      value={targetRole}
      onChange={(e) => setTargetRole(e.target.value)}
      placeholder="Target role (optional), e.g. Laravel Developer"
      className="mt-4 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
    />

    <div className="mt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Difficulty</p>
      <div className="inline-flex bg-gray-100 rounded-full p-1">
        {['easy', 'normal', 'hard'].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDifficulty(d)}
            disabled={loadingQuestions}
            className={`px-5 py-1.5 rounded-full text-sm font-semibold capitalize transition disabled:opacity-60 ${
              difficulty === d
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm'
                : 'text-gray-600 hover:text-purple-700'
            }`}
          >
            {d}
          </button>
        ))}
      </div>
    </div>

  <div className="mt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Job description (optional)</p>
      <textarea
        value={interviewJd}
        onChange={(e) => setInterviewJd(e.target.value)}
        rows={4}
        maxLength={4000}
        disabled={loadingQuestions}
        placeholder="Paste a job description to get questions tailored to that job..."
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:bg-gray-50"
      />
    </div>

    {questionsError && <p className="mt-3 text-sm text-red-500">{questionsError}</p>}

    <button
      onClick={handleStartMock}
      disabled={loadingQuestions}
      className="mt-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold text-sm shadow-sm hover:opacity-90 transition disabled:opacity-50 w-full sm:w-auto"
    >
      {loadingQuestions ? 'Preparing questions...' : 'Start Mock Interview'}
    </button>
  </div>
)}

{/* Mock Interview */}
{view === 'mock' && mockActive && questions.length > 0 && (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6 print:hidden">
    <div className="flex items-center justify-between gap-3 mb-4">
      <h3 className="font-bold text-base bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
        Mock Interview
      </h3>
      <button onClick={handleExitMock} className="text-xs text-gray-400 hover:text-gray-600">
        Exit
      </button>
    </div>

    {!mockDone ? (
      <>
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Question {mockIndex + 1} of {questions.length}</span>
            <span>{questions[mockIndex].category}</span>
          </div>
          <div className="h-1.5 bg-purple-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-pink-500 transition-all"
              style={{ width: `${((mockIndex + (mockFeedback ? 1 : 0)) / questions.length) * 100}%` }}
            />
          </div>
        </div>

        <p className="text-base font-semibold text-[#0D1B3D] mb-3">{questions[mockIndex].question}</p>

        <textarea
          value={mockAnswer}
          onChange={(e) => setMockAnswer(e.target.value)}
          rows={5}
          disabled={!!mockFeedback || mockLoading}
          placeholder="Type your answer here as if you are in a real interview..."
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent disabled:bg-gray-50"
        />

        {mockError && <p className="mt-2 text-sm text-red-500">{mockError}</p>}

        {!mockFeedback ? (
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSubmitAnswer}
              disabled={mockLoading || mockAnswer.trim().length < 5}
              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold text-sm shadow-sm hover:opacity-90 transition disabled:opacity-50 w-full sm:w-auto"
            >
              {mockLoading ? 'Evaluating...' : 'Submit Answer'}
            </button>
            <button
              onClick={handleSkipQuestion}
              disabled={mockLoading}
              className="text-sm text-gray-500 hover:text-gray-700 sm:self-center"
            >
              Skip question
            </button>
          </div>
        ) : (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <span
                className={`text-2xl font-extrabold tabular-nums ${
                  mockFeedback.score >= 7 ? 'text-[#10B981]' : mockFeedback.score >= 5 ? 'text-[#F59E0B]' : 'text-[#EF4444]'
                }`}
              >
                {mockFeedback.score}/10
              </span>
              <span className="text-xs text-gray-500">Your score for this answer</span>
            </div>

            {mockFeedback.strengths?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">What went well</p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {mockFeedback.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {mockFeedback.improvements?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">How to improve</p>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {mockFeedback.improvements.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {mockFeedback.sample_answer && (
              <div className="bg-purple-50/50 border border-purple-100 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sample better answer</p>
                <p className="text-sm text-gray-700">{mockFeedback.sample_answer}</p>
              </div>
            )}

            <button
              onClick={handleNextQuestion}
              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold text-sm shadow-sm hover:opacity-90 transition w-full sm:w-auto"
            >
              {mockIndex + 1 < questions.length ? 'Next Question' : 'Finish Interview'}
            </button>
          </div>
        )}
      </>
    ) : (
      <div>
        <div className="text-center mb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Overall Score</p>
          <p className="text-4xl font-extrabold text-[#0D1B3D] tabular-nums mt-1">{mockAverage}/10</p>
          <p className="text-xs text-gray-500 mt-1">
            Based on {mockScored.length} answered question{mockScored.length === 1 ? '' : 's'}
          </p>
        </div>

        <div className="space-y-2 mb-5">
          {mockResults.map((r, i) => r && (
            <div key={i} className="flex items-start justify-between gap-3 bg-gray-50 rounded-xl p-3">
              <p className="text-sm text-gray-700">{r.question}</p>
              <span className="text-sm font-bold text-[#0D1B3D] shrink-0">
                {r.score != null ? `${r.score}/10` : 'Skipped'}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleStartMock}
            className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-2 rounded-full font-semibold text-sm shadow-sm hover:opacity-90 transition w-full sm:w-auto"
          >
            Try Again
          </button>
          <button onClick={handleExitMock} className="text-sm text-gray-500 hover:text-gray-700 sm:self-center">
            Close
          </button>
        </div>
      </div>
    )}
  </div>
)}
        </>
        )}

                {view === 'resume' && (
        <>
        {/* Resume document */}
        <div className="flex items-center justify-between mb-3 print:hidden">
          <h3 className="font-bold text-base bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
            Your Resume
          </h3>
       
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-10 print:shadow-none print:rounded-none print:border-0">
         <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 border-b border-purple-100 pb-6 mb-6 text-center sm:text-left">
            {(() => {
              const photoUrl = getPhotoUrl(profile);
              return photoUrl ? (
                <img
                  src={photoUrl}
                  alt={profile.name}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-purple-100 shadow shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 text-white flex items-center justify-center text-2xl font-bold shadow shrink-0">
                  {getInitials(profile.name)}
                </div>
              );
            })()}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-purple-700">{profile.name}</h1>
              <p className="text-gray-600 text-sm mt-1 break-words">
                {[profile.email, profile.phone, profile.city && `${profile.city}, ${profile.state}`]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>

          {profile.career_objective && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-purple-700 border-b border-purple-100 pb-1 mb-2">Career Objective</h2>
              <p className="text-gray-700 text-sm">{profile.career_objective}</p>
            </div>
          )}

          {skills.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-purple-700 border-b border-purple-100 pb-1 mb-2">Skills</h2>
              <div className="flex flex-wrap gap-2">
                {skills.map((s) => (
                  <span key={s.id} className="bg-purple-50 text-purple-700 text-xs font-medium px-3 py-1 rounded-full">
                    {s.skill_name} ({s.proficiency})
                  </span>
                ))}
              </div>
            </div>
          )}

          {experiences.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-purple-700 border-b border-purple-100 pb-1 mb-2">Experience</h2>
              {experiences.map((exp) => (
                <div key={exp.id} className="mb-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                    <p className="font-semibold text-[#0D1B3D]">{exp.job_title}</p>
                    <p className="text-xs text-gray-500 shrink-0">
                      {exp.start_date} - {exp.currently_working ? 'Present' : exp.end_date}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">{exp.company_name}</p>
                  {exp.description && <p className="text-sm text-gray-700 mt-1">{exp.description}</p>}
                </div>
              ))}
            </div>
          )}

          {educations.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-purple-700 border-b border-purple-100 pb-1 mb-2">Education</h2>
              {educations.map((edu) => (
                <div key={edu.id} className="mb-3">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                    <p className="font-semibold text-[#0D1B3D]">{edu.degree}</p>
                    <p className="text-xs text-gray-500 shrink-0">
                      {edu.start_year} - {edu.end_year || 'Present'}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    {edu.institution} {edu.board_university && `· ${edu.board_university}`}
                  </p>
                  {edu.percentage_cgpa && (
                    <p className="text-xs text-gray-500">Score: {edu.percentage_cgpa}%</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {certifications.length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-purple-700 border-b border-purple-100 pb-1 mb-2">Certifications</h2>
              {certifications.map((cert) => (
                <div key={cert.id} className="mb-2">
                  <p className="font-semibold text-[#0D1B3D] text-sm">{cert.certificate_name}</p>
                  <p className="text-xs text-gray-500">{cert.issued_by} {cert.issue_date && `· ${cert.issue_date}`}</p>
                </div>
              ))}
            </div>
          )}

          {languages.length > 0 && (
            <div>
              <h2 className="text-lg font-bold text-purple-700 border-b border-purple-100 pb-1 mb-2">Languages</h2>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <span key={lang.id} className="bg-pink-50 text-pink-700 text-xs font-medium px-3 py-1 rounded-full">
                    {lang.language_name} ({lang.proficiency})
                  </span>
                ))}
              </div>
           </div>
          )}
        </div>
            </>
        )}
      </div>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
}

export default ResumeBuilder;